import { Label } from "@radix-ui/react-label";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { useNavigate, useOutletContext } from "react-router-dom";

import { CopyToClipboardButton } from "@/components/CopyToClipboardButton";
import { GameCreateDialog } from "@/components/dialogs/GameCreateDialog";
import { TrialEditDialog } from "@/components/dialogs/TrialEditDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FragmentType, getFragmentData } from "@/gen";
import { GameT, GameTable2_Game, TrialT } from "@/routes/trial/queries";

export default function TrialRoute() {
    const { trial } = useOutletContext<{ trial: TrialT; refetch: () => void }>();

    return (
        <Card className="bg-brand-white text-brand-black m-4 rounded border-none">
            <CardHeader>
                <CardTitle>
                    {trial.player1.nickname} vs. {trial.player2.nickname}
                </CardTitle>
                <div className="flex items-start justify-between">
                    <div className="flex flex-col">
                        <div className="flex-row items-center">
                            <Label className="text-sm font-medium">ID: </Label>
                            <>
                                {trial.id}
                                <CopyToClipboardButton text={trial.id} />
                            </>
                        </div>
                    </div>
                    <TrialEditDialog />
                </div>
            </CardHeader>
            <CardContent>
                <div className={"flex flex-col gap-4"}>
                    <div>
                        <Label className="text-md font-medium">Player 1</Label>
                        <p className="text-md text-muted-foreground">{trial.player1.name || "—"}</p>
                    </div>
                    <div>
                        <Label className="text-md font-medium">Player 2</Label>
                        <p className="text-md text-muted-foreground">{trial.player2.name || "—"}</p>
                    </div>
                    <GameTable gamesFragment={trial.games} />
                </div>
            </CardContent>
        </Card>
    );
}

const columns: ColumnDef<GameT>[] = [
    {
        accessorKey: "positionInTrial",
        header: () => (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="cursor-help border-b border-dashed border-gray-500">
                            Position in trial
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>
                        <div className="max-w-xs">
                            <p className="mb-1">Auto-computed ordering within trial</p>
                            <p className="text-xs">Games are sorted by:</p>
                            <ol className="mt-1 list-inside list-decimal space-y-0.5 text-xs">
                                <li>startedAt ascending</li>
                                <li>valid PGN round tags numerically</li>
                                <li>invalid/empty round tags last</li>
                                <li>createdAt as tiebreaker</li>
                            </ol>
                        </div>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        ),
        cell: ({ row }) => {
            return <div className="font-medium">{row.original.positionInTrial ?? "-"}.</div>;
        },
    },
    {
        accessorKey: "id",
        header: () => <div>ID</div>,
        cell: ({ row }) => {
            return <div>{row.original.id}</div>;
        },
    },
    {
        accessorKey: "whitePlayer",
        header: () => <div>White player</div>,
        cell: ({ row }) => {
            return <div>{row.original.whitePlayer.nickname}</div>;
        },
    },
    {
        accessorKey: "blackPlayer",
        header: () => <div>White player</div>,
        cell: ({ row }) => {
            return <div>{row.original.blackPlayer.nickname}</div>;
        },
    },
    {
        accessorKey: "scheduledStartAt",
        header: () => <div>Scheduled start at</div>,
        cell: ({ row }) => {
            return (
                <div className="font-medium">
                    {row.original.scheduledStartAt
                        ? format(row.original.scheduledStartAt, "PPpp")
                        : "-"}
                </div>
            );
        },
    },
    {
        accessorKey: "startedAt",
        header: () => <div>Started at</div>,
        cell: ({ row }) => {
            return (
                <div className="font-medium">
                    {row.original.startedAt ? format(row.original.startedAt, "PPpp") : "-"}
                </div>
            );
        },
    },
    {
        accessorKey: "pgnRoundTag",
        header: () => <div>PGN round tag</div>,
        cell: ({ row }) => {
            return <div className="font-medium">{row.original.pgnRoundTag ?? "-"}</div>;
        },
    },
    {
        accessorKey: "result",
        header: () => <div>Result</div>,
        cell: ({ row }) => {
            return <div>{row.original.result}</div>;
        },
    },
];

const GameTable = ({
    gamesFragment,
}: {
    gamesFragment: FragmentType<typeof GameTable2_Game>[];
}) => {
    const navigate = useNavigate();

    const games = getFragmentData(GameTable2_Game, gamesFragment);

    return (
        <div>
            <DataTable
                columns={columns}
                data={games}
                onRowClick={row => {
                    navigate(`games/${row.id}`);
                }}
                title="Games"
                addButton={<GameCreateDialog />}
            />
        </div>
    );
};
